"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useGetCatalogAttributions = void 0;
const react_1 = require("react");
const react_2 = require("@apollo/client/react");
const utils_1 = require("./utils");
const client_constants_1 = require("../../client.constants");
const client_utils_1 = require("../../client.utils");
const hooks_1 = require("../../hooks");
const get_catalog_attributions_query_1 = require("../gql/get-catalog-attributions.query");
const updateQuery = (previousResult, options) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    const prevCatalogs = (_b = (_a = previousResult === null || previousResult === void 0 ? void 0 : previousResult.catalogs) === null || _a === void 0 ? void 0 : _a.edges) !== null && _b !== void 0 ? _b : [];
    const fetchMoreCatalogs = (_e = (_d = (_c = options === null || options === void 0 ? void 0 : options.fetchMoreResult) === null || _c === void 0 ? void 0 : _c.catalogs) === null || _d === void 0 ? void 0 : _d.edges) !== null && _e !== void 0 ? _e : [];
    if (!prevCatalogs.length)
        return (_f = options === null || options === void 0 ? void 0 : options.fetchMoreResult) !== null && _f !== void 0 ? _f : previousResult;
    const prevNode = prevCatalogs[0].node;
    const nextNode = (_h = (_g = fetchMoreCatalogs[0]) === null || _g === void 0 ? void 0 : _g.node) !== null && _h !== void 0 ? _h : {};
    const prevEdges = (_k = (_j = prevNode === null || prevNode === void 0 ? void 0 : prevNode.attributions) === null || _j === void 0 ? void 0 : _j.edges) !== null && _k !== void 0 ? _k : [];
    const nextEdges = (_m = (_l = nextNode === null || nextNode === void 0 ? void 0 : nextNode.attributions) === null || _l === void 0 ? void 0 : _l.edges) !== null && _m !== void 0 ? _m : [];
    const merged = Object.assign(Object.assign({}, previousResult), { catalogs: Object.assign(Object.assign({}, previousResult === null || previousResult === void 0 ? void 0 : previousResult.catalogs), { edges: [
                Object.assign(Object.assign({}, prevCatalogs[0]), { node: Object.assign(Object.assign({}, prevNode), { attributions: Object.assign(Object.assign({}, nextNode.attributions), { edges: [...prevEdges, ...nextEdges] }) }) }),
            ] }) });
    return merged;
};
const fetchMoreOptions = { updateQuery };
/**
 * Hook: useGetCatalogAttributions
 *
 * Purpose:
 * - Fetch catalog attributions (attribute + type) for a single catalog with
 *   relay-style pagination and return a deduplicated array of CatalogAttribution.
 * - Keep this hook minimal: it should not perform UI mapping (id generation or
 *   isEditable flags). Consumers should map CatalogAttribution -> TableColumnAttribute
 *   via a shared mapper utility.
 *
 * @param GetCatalogAttributionsArgs
 */
const useGetCatalogAttributions = ({ catalogId, pageSize = client_constants_1.REQUEST_LIMIT_MEDIUM, filter, options, }) => {
    var _a, _b, _c, _d, _e;
    const { data, previousData, error, networkStatus, variables, fetchMore, refetch, } = (0, react_2.useQuery)(get_catalog_attributions_query_1.GET_CATALOG_ATTRIBUTIONS, {
        notifyOnNetworkStatusChange: true,
        variables: {
            catalogId,
            filter,
            first: pageSize,
            before: null,
            after: null,
            last: null,
        },
        skip: !catalogId || (options === null || options === void 0 ? void 0 : options.skip),
    });
    const catalogNode = (_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.catalogs) === null || _a === void 0 ? void 0 : _a.edges) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.node;
    const { pageInfo } = (_d = catalogNode === null || catalogNode === void 0 ? void 0 : catalogNode.attributions) !== null && _d !== void 0 ? _d : {};
    const hasNextPage = (_e = pageInfo === null || pageInfo === void 0 ? void 0 : pageInfo.hasNextPage) !== null && _e !== void 0 ? _e : false;
    const pagination = (0, hooks_1.usePagination)({
        networkStatus,
        pageInfo,
        pageSize,
        fetchMore,
        fetchMoreOptions,
        lastVariables: variables,
    });
    const onRefetch = (0, react_1.useCallback)((newVariables) => refetch(Object.assign(Object.assign({}, newVariables), { first: pageSize, before: null, after: null, last: null })), [pageSize, refetch]);
    const attributesList = (0, react_1.useMemo)(() => (0, utils_1.getCatalogAttributionsFromData)(data), [data]);
    const previousAttributesList = (0, react_1.useMemo)(() => (0, utils_1.getCatalogAttributionsFromData)(previousData), [previousData]);
    return {
        data: attributesList,
        previousData: previousAttributesList,
        isLoading: (0, client_utils_1.isQueryLoadingOrRefetching)(networkStatus),
        isRefetching: (0, client_utils_1.isQueryRefetching)(networkStatus),
        pagination,
        error,
        hasNextPage,
        refetch: onRefetch,
    };
};
exports.useGetCatalogAttributions = useGetCatalogAttributions;
exports.default = exports.useGetCatalogAttributions;
